import React from 'react';
import { Printer, ArrowLeft } from 'react-feather';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { intlShape } from 'react-intl';
import { useMutation } from '@apollo/client';

import { StyledLinkButton, StyledH1 } from 'pages/QuoteConfirmation/styles';
import { CANCEL_BOOKING } from 'store/mutations';
import { ACTION_CODES } from 'constants/errors';

import AgentPortalError from 'errors/AgentPortalError';
import SendQuoteConfirmationEmailButton from 'pages/BookingDetails/Payments/SendQuoteConfirmationEmailButton';
import { Page, Row, Column } from 'components/shared/Layout';
import { CancelButton } from 'components/ui/Button';
import { bookingShape } from 'constants/propTypes';
import HideWhenPrinted from 'components/shared/HideWhenPrinted';
import BookingDetails from 'pages/QuoteConfirmation/BookingDetails';
import JourneyDetails from 'pages/QuoteConfirmation/JourneyDetails';
import PaymentDetails from 'pages/QuoteConfirmation/PaymentDetails';
import PriceBreakdown from 'pages/QuoteConfirmation/PriceBreakdown';
import ReviewQuoteFooter from './ReviewQuoteFooter';

const ReviewQuote = ({ booking, intl }) => {
  const history = useHistory();
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const membershipNumberUrl = membershipNumber ? `?membershipNumber=${membershipNumber}` : '';
  const [cancelBooking, { error: cancelBookingError, loading: cancelBookingLoading }] = useMutation(CANCEL_BOOKING);
  const handleRedirect = () => history.push(`/booking/${booking.reference}/review-booking${membershipNumberUrl}`);

  const handleReleaseSeats = async () => {
    await cancelBooking({
      variables: { bookingReference: booking.reference }
    });

    return history.push(`/search${membershipNumberUrl}`);
  };

  if (cancelBookingError) {
    throw new AgentPortalError(cancelBookingError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/booking/${booking.reference}/review-booking${membershipNumberUrl}`
    });
  }

  return (
    <Page>
      <Row span={14} start={2}>
        <Row alignItems="center">
          <Column span={10}>
            <HideWhenPrinted>
              <StyledLinkButton onClick={handleRedirect} data-testid="reviewQuote-backToReviewBooking">
                <ArrowLeft />
                <FormattedMessage id="reviewQuote.backToReviewBooking" />
              </StyledLinkButton>
            </HideWhenPrinted>
          </Column>
          <Column span={6} justifyContent="flex-end">
            <HideWhenPrinted>
              <StyledLinkButton onClick={window.print} data-testid="reviewQuote-downloadOrPrintQuote">
                <Printer />
                <FormattedMessage id="reviewQuote.downloadOrPrintQuote" />
              </StyledLinkButton>
            </HideWhenPrinted>

            <HideWhenPrinted>
              <SendQuoteConfirmationEmailButton booking={booking} quotationStatus={'submit'} disabled={false} isReviewQuote />
            </HideWhenPrinted>

            <HideWhenPrinted>
              <CancelButton
                data-testid="reviewQuote-cancelBooking"
                data-cy="review-quote-release-seats"
                onClick={handleReleaseSeats}
                loading={cancelBookingLoading}
                disabled={cancelBookingLoading}
              >
                <FormattedMessage id="reviewQuote.ReleaseSeats" />
              </CancelButton>
            </HideWhenPrinted>
          </Column>
        </Row>

        <StyledH1>
          <FormattedMessage
            id={'reviewQuote.title'}
            values={{
              groupName: booking?.customer?.companyName ?? ''
            }}
          />
        </StyledH1>
        <div>
          <p>
            <FormattedMessage id="quoteConfirmation.intro.greeting" />,
          </p>
          <p>
            <FormattedMessage id="reviewQuote.staticIntroTwo" />
          </p>
          <p>
            <FormattedMessage id="reviewQuote.staticIntroThree" />
          </p>
        </div>
        <BookingDetails booking={booking} isReviewQuote />
        <JourneyDetails booking={booking} intl={intl} />
        <PaymentDetails intl={intl} booking={booking} isReviewQuote />
        <PriceBreakdown booking={booking} intl={intl} />
        <ReviewQuoteFooter intl={intl} />
      </Row>
    </Page>
  );
};

ReviewQuote.propTypes = {
  booking: bookingShape.isRequired,
  intl: intlShape.isRequired
};

export default injectIntl(ReviewQuote);
